import type Quill from 'quill';
import { useEffect, useState } from 'react';

import { useLatest } from '#/hooks/useLatest';
import { useI18n } from '#/i18n/hooks/useI18n';
import type { ListingType } from '#/types/ListingType';

import { LISTING_RTE_ATS_WARNINGS } from './listingRteAtsWarnings';
import type { ListingRteAtsWarningType } from './types/ListingRteAtsWarningType';

type Args = {
  enabled: boolean;
  listingType?: ListingType | undefined;
  quill: Quill | undefined;
};

export function useListingRteWarning({ enabled, listingType, quill }: Args) {
  const { i18n } = useI18n();
  const [warningType, setWarningType] = useState<ListingRteAtsWarningType>();

  const [warningDismissed, setWarningDismissed] = useState(false);

  const linkNotificationDismissedRef = useLatest(warningDismissed);

  useEffect(() => {
    if (!quill || !enabled) return;

    quill.on('text-change', () => {
      if (linkNotificationDismissedRef.current) return;

      const html = quill.getSemanticHTML();

      const newWarningType = (() => {
        const containsEmail = html.includes('href="mailto:');
        const containsUrl = /href="[^m]/.test(html);

        if (containsEmail && containsUrl) return 'BOTH';
        if (containsEmail) return 'EMAIL';
        if (containsUrl) return 'URL';
      })();

      setWarningType(newWarningType);
    });
  }, [enabled, linkNotificationDismissedRef, quill]);

  if (warningDismissed || !warningType) return;

  return {
    text: LISTING_RTE_ATS_WARNINGS(i18n, listingType)[warningType],
    onDismiss: () => setWarningDismissed(true),
  };
}
